<div utrecht-document>
  <utrecht-page>
    <utrecht-page-content>
      <main>
        <utrecht-article>
          <h1>NL Design System met Angular</h1>
          <p>
            Van bijna alle componenten in de NL Design System community is herbruikbare CSS beschikbaar. Voor Angular is
            een gedeelte beschikbaar als component. Dat betekent dat je niet meer zelf alle CSS class names hoeft te
            schrijven, en je code-editor autocomplete heeft voor diverse properties.
          </p>
          <p>
            Componenten die nog niet voor Angular beschikbaar zijn, kun je waarschijnlijk relatief snel maken door
            gebruik te maken van de CSS die al wel bestaat.
          </p>
          <p>
            We hebben extra aandacht besteed aan de formulier-componenten, zodat ze werken met Angular form bindings.
          </p>
          <p>
            In Angular worden componenten vaak gerendered met een custom element als wrapper. Voor sommige componenten
            levert dit problemen op, bijvoorbeeld wanneer in HTML bepaalde elementen een directe parent-child-relatie
            moeten hebben. Denk aan: <code>ol</code>/<code>li</code>,
            <code>table</code>/<code>tbody</code>/<code>tr</code>/<code>td</code>, of
            <code>fieldset</code>/<code>legend</code>. We maken nu voor veel elementen gebruik van attribute selectors,
            om te voorkomen dat er een extra element wordt gemaakt. In de toekomst worden mogelijk bijna alle
            componenten beschikbaar via attribute selectors.
          </p>
          <pre>
&lt;table utrecht-table&gt;
  &lt;tbody utrecht-table-body&gt;
    &lt;tr utrecht-table-row&gt;
      &lt;td utrecht-table-cell&gt;...</pre
          >
          <p utrecht-paragraph>
            Bekijk de broncode op GitHub:
            <a href="https://github.com/nl-design-system/example-with-angular" utrecht-link [external]="true"
              >nl-design-system/example-with-angular</a
            >
          </p>
          <section>
            <example-story name="Heading 1" id="angular-utrecht-heading-1">
              <utrecht-heading-1>The Quick Brown Fox Jumps Over The Lazy Dog</utrecht-heading-1>
            </example-story>
            <example-story name="Heading 2" id="angular-utrecht-heading-2">
              <utrecht-heading-2>The Quick Brown Fox Jumps Over The Lazy Dog</utrecht-heading-2>
            </example-story>
            <example-story name="Heading 3" id="angular-utrecht-heading-3">
              <utrecht-heading-3>The Quick Brown Fox Jumps Over The Lazy Dog</utrecht-heading-3>
            </example-story>
            <example-story name="Heading 4" id="angular-utrecht-heading-4">
              <utrecht-heading-4>The Quick Brown Fox Jumps Over The Lazy Dog</utrecht-heading-4>
            </example-story>
            <example-story name="Heading 5" id="angular-utrecht-heading-5">
              <utrecht-heading-5>The Quick Brown Fox Jumps Over The Lazy Dog</utrecht-heading-5>
            </example-story>
            <example-story name="Heading 6" id="angular-utrecht-heading-6">
              <utrecht-heading-6>The Quick Brown Fox Jumps Over The Lazy Dog</utrecht-heading-6>
            </example-story>
            <example-story name="Ordered list" id="angular-utrecht-ordered-list">
              <ol utrecht-ordered-list>
                <li utrecht-ordered-list-item>Ordered Item 1</li>
              </ol>
            </example-story>
            <example-story name="Separator" id="angular-utrecht-separator">
              <utrecht-separator></utrecht-separator>
            </example-story>
            <example-story name="Unordered list" id="angular-utrecht-unordered-list">
              <ul utrecht-unordered-list>
                <li utrecht-unordered-list-item>Unordered Item 1</li>
              </ul>
            </example-story>
            <example-story name="Form label" id="angular-utrecht-form-label">
              <label utrecht-form-label>Form label</label>
            </example-story>
            <example-story name="Form label for radio button option" id="angular-utrecht-form-label--radio">
              <label utrecht-form-label type="radio">Form label</label>
            </example-story>
            <example-story name="Form label for checkbox option" id="angular-utrecht-form-label--checkbox">
              <label utrecht-form-label type="checkbox">Form label</label>
            </example-story>
            <example-story name="Form label for disabled form control" id="angular-utrecht-form-label--disabled">
              <label utrecht-form-label [disabled]="true">Form label</label>
            </example-story>
            <example-story name="Form label for checked radio button" id="angular-utrecht-form-label--checked">
              <label utrecht-form-label type="radio" [checked]="true">Form label</label>
            </example-story>
            <example-story name="Form label for checked checkbox button" id="angular-utrecht-form-label--checked-radio">
              <label utrecht-form-label type="checkbox" [checked]="true">Form label</label>
            </example-story>
            <example-story name="Textbox" id="angular-utrecht-textbox">
              <input type="text" utrecht-textbox />
            </example-story>
            <example-story name="Disabled textbox" id="angular-utrecht-textbox--disabled">
              <input type="text" utrecht-textbox [disabled]="true" />
            </example-story>
            <example-story name="Read-only textbox" id="angular-utrecht-textbox--read-only">
              <input type="text" utrecht-textbox [readOnly]="true" />
            </example-story>
            <example-story name="Invalid textbox" id="angular-utrecht-textbox--invalid">
              <input type="text" utrecht-textbox [invalid]="true" />
            </example-story>
            <example-story name="Required textbox">
              <input type="text" utrecht-textbox [required]="true" id="angular-utrecht-textbox--required" />
            </example-story>
            <example-story name="Textbox with placeholder FIXME" id="angular-utrecht-textbox--placeholder">
              <input type="text" utrecht-textbox placeholder="Placeholder" />
            </example-story>
            <example-story name="Textarea" id="angular-utrecht-textarea">
              <textarea utrecht-textarea></textarea>
            </example-story>
            <example-story name="Disabled textarea" id="angular-utrecht-textarea--disabled">
              <textarea utrecht-textarea [disabled]="true"></textarea>
            </example-story>
            <example-story name="Read-only textarea" id="angular-utrecht-textarea--read-only">
              <textarea utrecht-textarea [readOnly]="true"></textarea>
            </example-story>
            <example-story name="Invalid textarea" id="angular-utrecht-textarea--invalid">
              <textarea utrecht-textarea [invalid]="true"></textarea>
            </example-story>
            <example-story name="Required textarea" id="angular-utrecht-textarea--required">
              <textarea utrecht-textarea [required]="true"></textarea>
            </example-story>
            <example-story name="Textarea with placeholder" id="angular-utrecht-textarea--placeholder">
              <textarea utrecht-textarea placeholder="Placeholder"></textarea>
            </example-story>
            <fieldset utrecht-form-fieldset>
              <legend utrecht-form-fieldset-legend>Checkbox group</legend>
              <utrecht-form-field>
                <label utrecht-form-label type="checkbox" for="b14381ec-d517-4a2e-9d37-e920501fbe93">Option 1</label>
                <input type="checkbox" utrecht-checkbox id="b14381ec-d517-4a2e-9d37-e920501fbe93" value="1" />
              </utrecht-form-field>
              <utrecht-form-field>
                <label utrecht-form-label type="checkbox" for="d404ad81-6f6e-48e9-a0d2-bd9c38a333d7">Option 2</label>
                <input type="checkbox" utrecht-checkbox id="d404ad81-6f6e-48e9-a0d2-bd9c38a333d7" value="2" />
              </utrecht-form-field>
              <utrecht-form-field>
                <label utrecht-form-label type="checkbox" for="f35c996f-8617-442a-85b5-80a5086d97c9" [disabled]="true"
                  >Disabled option 3</label
                >
                <input
                  type="checkbox"
                  utrecht-checkbox
                  id="f35c996f-8617-442a-85b5-80a5086d97c9"
                  value="3"
                  [disabled]="true"
                />
              </utrecht-form-field>
              <utrecht-form-field>
                <label utrecht-form-label type="checkbox" for="e8b3c516-76eb-40bb-835e-282b60782028"
                  >Required option 4</label
                >
                <input
                  type="checkbox"
                  utrecht-checkbox
                  id="e8b3c516-76eb-40bb-835e-282b60782028"
                  value="4"
                  [required]="true"
                />
              </utrecht-form-field>
              <utrecht-form-field>
                <label utrecht-form-label type="checkbox" for="61cff474-ee09-4571-9896-1ba1291f1ffa"
                  >Invalid option 5</label
                >
                <input
                  type="checkbox"
                  utrecht-checkbox
                  id="61cff474-ee09-4571-9896-1ba1291f1ffa"
                  value="5"
                  [invalid]="true"
                />
              </utrecht-form-field>
            </fieldset>
            <example-story name="External link">
              <a href="https://example.com" utrecht-link [external]="true">Example.com</a>
            </example-story>
            <example-story name="Link to the previous page in a series of pages">
              <a href="#prev" utrecht-link rel="prev">Example.com</a>
            </example-story>
            <example-story name="Link">
              <a href="https://example.com" utrecht-link>Example.com</a>
            </example-story>
            <example-story name="Button" id="angular-utrecht-button">
              <button utrecht-button>Default</button>
            </example-story>
            <example-story name="Primary action button" id="angular-utrecht-button--primary-action">
              <button utrecht-button appearance="primary-action-button">Primary action</button>
            </example-story>
            <example-story name="Secondary action button" id="angular-utrecht-button--secondary-action">
              <button utrecht-button appearance="secondary-action-button">Secondary action</button>
            </example-story>
            <example-story name="Subtle button" id="angular-utrecht-button--subtle">
              <button utrecht-button appearance="subtle-action-button">Subtle</button>
            </example-story>
            <example-story name="Disabled button" id="angular-utrecht-button--disabled">
              <button utrecht-button type="submit" [disabled]="true">Disabled</button>
            </example-story>
            <example-story name="Submit button" id="angular-utrecht-button--submit">
              <button utrecht-button type="submit">Submit</button>
            </example-story>
            <example-story name="Busy button" id="angular-utrecht-button--busy">
              <button utrecht-button [busy]="true">Busy</button>
            </example-story>
            <example-story name="Button group" id="angular-utrecht-button-group">
              <utrecht-button-group>
                <button utrecht-button appearance="primary-action-button">Submit request</button>
                <button utrecht-button appearance="secondary-action-button">Save and continue later</button>
              </utrecht-button-group>
            </example-story>
            <example-story name="Table" id="angular-utrecht-table">
              <table utrecht-table>
                <caption utrecht-table-caption>
                  Table caption
                </caption>
                <thead utrecht-table-header>
                  <tr utrecht-table-row>
                    <th utrecht-table-header-cell scope="column">Column header cell 1</th>
                    <th utrecht-table-header-cell scope="column">Column header cell 2</th>
                    <th utrecht-table-header-cell scope="column">Column header cell 3</th>
                  </tr>
                </thead>
                <tfoot utrecht-table-footer>
                  <tr utrecht-table-row>
                    <th utrecht-table-cell>Footer cell 1</th>
                    <th utrecht-table-cell>Footer cell 2</th>
                    <th utrecht-table-cell>Footer cell 3</th>
                  </tr>
                </tfoot>
                <tbody utrecht-table-body>
                  <tr utrecht-table-row>
                    <th utrecht-table-header-cell scope="row">Row header cell</th>
                    <td utrecht-table-cell>Table cell 1</td>
                    <td utrecht-table-cell>Table cell 2</td>
                  </tr>
                </tbody>
              </table>
            </example-story>
            <section>
              <h2>Badge</h2>
              <example-story name="Neutral badge">
                <utrecht-badge-status type="neutral">neutral</utrecht-badge-status>
              </example-story>
              <example-story name="Danger badge">
                <utrecht-badge-status type="danger">danger</utrecht-badge-status>
              </example-story>
              <example-story nmae="Warning badge">
                <utrecht-badge-status type="warning">warning</utrecht-badge-status>
              </example-story>
              <example-story name="Safe badge">
                <utrecht-badge-status type="safe">safe</utrecht-badge-status>
              </example-story>
              <example-story name="Invalid badge">
                <utrecht-badge-status type="invalid">invalid</utrecht-badge-status>
              </example-story>
              <example-story name="Valid badge">
                <utrecht-badge-status type="valid">valid</utrecht-badge-status>
              </example-story>
              <example-story name="Error badge">
                <utrecht-badge-status type="error">error</utrecht-badge-status>
              </example-story>
              <example-story name="Success badge">
                <utrecht-badge-status type="success">success</utrecht-badge-status>
              </example-story>
              <example-story name="Inactive badge">
                <utrecht-badge-status type="inactive">inactive</utrecht-badge-status>
              </example-story>
              <example-story name="Active badge">
                <utrecht-badge-status type="active">active</utrecht-badge-status>
              </example-story>
            </section>
            <fieldset>
              <legend>Radio group</legend>
              <utrecht-form-field>
                <input type="radio" utrecht-radio-button id="option-a" name="option" value="A" />
                <label for="option-a" utrecht-form-label>Option A</label>
              </utrecht-form-field>
              <utrecht-form-field>
                <input type="radio" utrecht-radio-button id="option-b" name="option" value="B" />
                <label for="option-b" utrecht-form-label>Option B</label>
              </utrecht-form-field>
            </fieldset>
            <fieldset utrecht-form-fieldset>
              <legend utrecht-form-fieldset-legend>Angular reactive forms</legend>
              <utrecht-button-group>
                <button utrecht-button>Default</button>
                <button utrecht-button appearance="primary-action-button">Primary action</button>
                <button utrecht-button appearance="secondary-action-button">Secondary action</button>
                <button utrecht-button appearance="subtle-action-button">Subtle</button>
                <button utrecht-button type="submit" [disabled]="true">Disabled</button>
                <button utrecht-button type="submit">Submit</button>
                <button utrecht-button [busy]="true">Busy</button>
              </utrecht-button-group>
              <utrecht-html-content>
                <table utrecht-table>
                  <thead utrecht-table-header>
                    <tr utrecht-table-row>
                      <th utrecht-table-header-cell scope="column">Component</th>
                      <th utrecht-table-header-cell scope="column">Example</th>
                      <th utrecht-table-header-cell scope="column">Value</th>
                    </tr>
                  </thead>
                  <tbody utrecht-table-body>
                    <caption utrecht-table-caption>
                      Tabel
                    </caption>
                    <tr utrecht-table-row>
                      <th utrecht-table-header-cell scope="row"><code>utrecht-textbox</code></th>
                      <td utrecht-table-cell>
                        <label utrecht-form-label for="14d07fbb-0890-41b3-a9f2-2bff1ad100d0">Name</label>
                        <input utrecht-textbox [formControl]="textbox" id="14d07fbb-0890-41b3-a9f2-2bff1ad100d0" />
                      </td>
                      <td utrecht-table-cell>{{ textbox.value }}</td>
                    </tr>
                    <tr utrecht-table-row>
                      <th utrecht-table-header-cell scope="row"><code>utrecht-textbox</code>, required</th>
                      <td utrecht-table-cell>
                        <input utrecht-textbox [formControl]="textbox" [required]="true" />
                      </td>
                      <td utrecht-table-cell>{{ textbox.value }}</td>
                    </tr>
                    <tr utrecht-table-row>
                      <th utrecht-table-header-cell scope="row"><code>utrecht-textbox</code>, disabled</th>
                      <td utrecht-table-cell>
                        <input utrecht-textbox [formControl]="textbox" [disabled]="true" />
                      </td>
                      <td utrecht-table-cell>{{ textbox.value }}</td>
                    </tr>
                    <tr utrecht-table-row>
                      <th utrecht-table-header-cell scope="row"><code>utrecht-textbox</code>, readonly</th>
                      <td utrecht-table-cell>
                        <input utrecht-textbox [formControl]="textbox" [readonly]="true" />
                      </td>
                      <td utrecht-table-cell>{{ textbox.value }}</td>
                    </tr>
                    <tr utrecht-table-row>
                      <th utrecht-table-header-cell scope="row"><code>utrecht-textbox</code>, invalid</th>
                      <td utrecht-table-cell>
                        <input utrecht-textbox [formControl]="textbox" [invalid]="true" />
                      </td>
                      <td utrecht-table-cell>{{ textbox.value }}</td>
                    </tr>
                    <tr utrecht-table-row>
                      <th utrecht-table-header-cell scope="row"><code>utrecht-textbox</code>, type=email</th>
                      <td utrecht-table-cell>
                        <input utrecht-textbox [formControl]="email" type="email" />
                      </td>
                      <td utrecht-table-cell>{{ email.value }}</td>
                    </tr>
                    <tr utrecht-table-row>
                      <th utrecht-table-header-cell scope="row"><code>utrecht-textbox</code>, type=url</th>
                      <td utrecht-table-cell>
                        <input utrecht-textbox [formControl]="url" type="url" />
                      </td>
                      <td utrecht-table-cell>{{ url.value }}</td>
                    </tr>
                    <tr utrecht-table-row>
                      <th utrecht-table-header-cell scope="row"><code>utrecht-textbox</code>, type=number</th>
                      <td utrecht-table-cell>
                        <input utrecht-textbox [formControl]="number" type="number" />
                      </td>
                      <td utrecht-table-cell>{{ number.value }}</td>
                    </tr>
                    <tr utrecht-table-row>
                      <th utrecht-table-header-cell scope="row"><code>utrecht-textbox</code>, inputmode=number</th>
                      <td utrecht-table-cell>
                        <input utrecht-textbox [formControl]="number" inputmode="number" />
                      </td>
                      <td utrecht-table-cell>{{ number.value }}</td>
                    </tr>
                    <tr utrecht-table-row>
                      <th utrecht-table-header-cell scope="row"><code>utrecht-textarea</code></th>
                      <td utrecht-table-cell>
                        <textarea utrecht-textarea [formControl]="textarea"></textarea>
                      </td>
                      <td utrecht-table-cell>{{ textarea.value }}</td>
                    </tr>
                    <tr utrecht-table-row>
                      <th utrecht-table-header-cell scope="row"><code>utrecht-textarea</code>, required</th>
                      <td utrecht-table-cell>
                        <textarea utrecht-textarea [formControl]="textarea" [required]="true"></textarea>
                      </td>
                      <td utrecht-table-cell>{{ textarea.value }}</td>
                    </tr>
                    <tr utrecht-table-row>
                      <th utrecht-table-header-cell scope="row"><code>utrecht-textarea</code>, disabled</th>
                      <td utrecht-table-cell>
                        <textarea utrecht-textarea [formControl]="textarea" [disabled]="true"></textarea>
                      </td>
                      <td utrecht-table-cell>{{ textarea.value }}</td>
                    </tr>
                    <tr utrecht-table-row>
                      <th utrecht-table-header-cell scope="row"><code>utrecht-textarea</code>, readonly</th>
                      <td utrecht-table-cell>
                        <textarea utrecht-textarea [formControl]="textarea" [readonly]="true"></textarea>
                      </td>
                      <td utrecht-table-cell>{{ textarea.value }}</td>
                    </tr>
                    <tr utrecht-table-row>
                      <th utrecht-table-header-cell scope="row"><code>utrecht-textarea</code>, invalid</th>
                      <td utrecht-table-cell>
                        <textarea utrecht-textarea [formControl]="textarea" [invalid]="true"></textarea>
                      </td>
                      <td utrecht-table-cell>{{ textarea.value }}</td>
                    </tr>
                    <tr utrecht-table-row>
                      <th utrecht-table-header-cell scope="row"><code>utrecht-checkbox</code></th>
                      <td utrecht-table-cell>
                        <input type="checkbox" utrecht-checkbox [formControl]="checkbox" />
                      </td>
                      <td utrecht-table-cell>{{ checkbox.value }}</td>
                    </tr>
                    <tr utrecht-table-row>
                      <th utrecht-table-header-cell scope="row"><code>utrecht-form-field-textbox</code></th>
                      <td utrecht-table-cell>
                        <!-- <utrecht-form-field-textbox [formControl]="textbox">Example Name</utrecht-form-field-textbox> -->
                      </td>
                      <td utrecht-table-cell>{{ textbox.value }}</td>
                    </tr>
                    <tr utrecht-table-row>
                      <th utrecht-table-header-cell scope="row"><code>utrecht-form-field-checkbox</code></th>
                      <td utrecht-table-cell>
                        <!-- <utrecht-form-field-checkbox [formControl]="checkbox">Example Checkbox</utrecht-form-field-checkbox> -->
                      </td>
                      <td utrecht-table-cell>{{ checkbox.value }}</td>
                    </tr>
                    <tr utrecht-table-row>
                      <th utrecht-table-header-cell scope="row"><code>utrecht-radio-button</code></th>
                      <td utrecht-table-cell>
                        <utrecht-form-field>
                          <label for="dd122ad5-3ef9-4120-b98b-ff8cac9f604e" utrecht-form-label>Option A</label>
                          <input
                            type="radio"
                            utrecht-radio-button
                            [formControl]="radio"
                            value="A"
                            id="dd122ad5-3ef9-4120-b98b-ff8cac9f604e"
                          />
                        </utrecht-form-field>
                        <utrecht-form-field>
                          <label for="f02359c9-2eb8-47cb-a471-227038330ba6" utrecht-form-label>Option B</label>
                          <input
                            type="radio"
                            utrecht-radio-button
                            [formControl]="radio"
                            value="B"
                            id="f02359c9-2eb8-47cb-a471-227038330ba6"
                          />
                        </utrecht-form-field>
                      </td>
                      <td utrecht-table-cell>{{ radio.value }}</td>
                    </tr>
                  </tbody>
                </table>
              </utrecht-html-content>
            </fieldset>
          </section>
          <example-story name="Paragraph" id="angular-utrecht-paragraph">
            <p utrecht-paragraph>paragraph</p>
          </example-story>
          <example-story name="Lead paragraph" id="angular-utrecht-paragraph--lead">
            <p utrecht-paragraph [lead]="true">paragraph</p>
          </example-story>
        </utrecht-article>
      </main>
    </utrecht-page-content>
    <utrecht-page-footer>
      <p>Footer</p>
      <div>
        <nl-theme-switcher></nl-theme-switcher>
      </div>
    </utrecht-page-footer>
  </utrecht-page>
</div>
